import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BranchService } from '../../common/_services/branch.service';
import { PrinterEndpointsService } from '../../common/_services/printer-endpoints.service';

@Component({
  selector: 'app-medibrix-check-in',
  templateUrl: './medibrix-check-in.component.html',
  styleUrls: ['./medibrix-check-in.component.scss'],
})
export class MedibrixCheckInComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() isVisible: boolean = false;
  @Input() printerQueue: any;
  @Output() close = new EventEmitter<void>();
  @Output() bookingCodeSubmitted = new EventEmitter<any>();
  @Output() dropInSelected = new EventEmitter<void>();

  @ViewChild('bookingCodeInput', { static: false }) bookingCodeInput: ElementRef<HTMLInputElement>;

  currentStep: string = 'initial';
  bookingCodeForm: FormGroup;
  isTransitioning: boolean = false;
  showKeypad: boolean = true;
  submitted: boolean = false;
  isLoading: boolean = false;
  bookingError: string = '';

  private autoCloseTimerId: any = null;
  // Guard against synthetic/double events on iOS WebView
  private lastPressTime: number = 0;
  private lastPressKey: string | null = null;
  private readonly PRESS_DEBOUNCE_MS = 220; // short window to ignore duplicates (covers WKWebView synthetic click delay)

  readonly STEPS = {
    INITIAL: 'initial',
    BOOKING_CODE: 'booking_code',
  };

  constructor(
    private formBuilder: FormBuilder,
    public branchService: BranchService,
    private printerEndpointsService: PrinterEndpointsService
  ) {
    this.bookingCodeForm = this.formBuilder.group({
      // No validators; numeric restriction handled by UI and maxlength.
      bookingCode: [''],
    });
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible']) {
      const visible = changes['isVisible'].currentValue;
      if (visible) {
        // Start timer based on current step when modal opens (initial -> 30s)
        this.startAutoCloseTimerForCurrentStep();
      } else {
        // Modal hidden -> clear any existing timer
        this.clearAutoCloseTimer();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.currentStep === this.STEPS.BOOKING_CODE && this.bookingCodeInput) {
      setTimeout(() => {
        this.bookingCodeInput.nativeElement.focus();
      }, 100);
    }
  }

  onModalBackgroundClick(): void {
    this.closeModal();
  }

  onHaveBookingCodeClick(): void {
    this.currentStep = this.STEPS.BOOKING_CODE;
    setTimeout(() => {
      if (this.bookingCodeInput) {
        this.bookingCodeInput.nativeElement.focus();
      }
    }, 100);
    // ensure enabled when entering step
    const ctrl = this.bookingCodeForm.get('bookingCode');
    ctrl?.enable({ emitEvent: false });
    this.startAutoCloseTimerForCurrentStep();
  }

  onDropInClick(): void {
    this.dropInSelected.emit();
    this.closeModal();
  }

  onBackClick(): void {
    this.isTransitioning = true;

    setTimeout(() => {
      this.currentStep = this.STEPS.INITIAL;
      this.bookingCodeForm.reset();
      this.submitted = false;
      this.bookingError = '';
      this.startAutoCloseTimerForCurrentStep();
      setTimeout(() => {
        this.isTransitioning = false;
      }, 200);
    }, 150);
  }

  onConfirmBookingCode(): void {
    this.resetAutoCloseTimerForCurrentStep();
    this.submitted = true;
    this.bookingError = '';
    const bookingCodeControl = this.bookingCodeForm.get('bookingCode');
    const bookingCode = bookingCodeControl ? bookingCodeControl.value : '';

    this.isTransitioning = true;
    this.setLoading(true);

    this.printerEndpointsService.createTokenViaBooking(bookingCode).subscribe(
      (response: any) => {
        this.setLoading(false);
        this.closeModal();
        this.bookingCodeSubmitted.emit(response);
      },
      (error) => {
        this.setLoading(false);
        this.isTransitioning = false;
        this.bookingError = 'booking_invalidCheckInCodeServer';
        console.error('Error creating token via booking:', error);
      }
    );
  }

  onDigitPress(d: string): void {
    if (this.shouldIgnorePress(`digit:${d}`)) return;
    this.submitted = false;
    const ctrl = this.bookingCodeForm.get('bookingCode');
    if (!ctrl) return;
    const current = ctrl.value || '';
    if (current.length >= 6) return;
    ctrl.setValue(current + d, { emitEvent: false });
    this.resetAutoCloseTimerForCurrentStep();
  }

  onDeleteDigit(): void {
    if (this.shouldIgnorePress('delete')) return;
    this.submitted = false;
    const ctrl = this.bookingCodeForm.get('bookingCode');
    if (!ctrl) return;
    const current = ctrl.value || '';
    ctrl.setValue(current.slice(0, -1), { emitEvent: false });
    this.resetAutoCloseTimerForCurrentStep();
  }

  clearAll(): void {
    if (this.shouldIgnorePress('clear')) return;
    this.submitted = false;
    const ctrl = this.bookingCodeForm.get('bookingCode');
    if (!ctrl) return;
    ctrl.setValue('', { emitEvent: false });
    this.resetAutoCloseTimerForCurrentStep();
  }

  closeModal(): void {
    this.clearAutoCloseTimer();
    this.currentStep = this.STEPS.INITIAL;
    this.bookingCodeForm.reset();
    this.submitted = false;
    this.isTransitioning = false;
    this.setLoading(false);
    this.close.emit();
    this.bookingError = '';
  }

  private setLoading(value: boolean): void {
    this.isLoading = value;
    const ctrl = this.bookingCodeForm.get('bookingCode');
    if (ctrl) {
      if (value) {
        ctrl.disable({ emitEvent: false });
      } else {
        ctrl.enable({ emitEvent: false });
      }
    }
  }

  private startAutoCloseTimerForCurrentStep(): void {
    const duration = this.currentStep === this.STEPS.INITIAL ? 30000 : 90000;
    this.startAutoCloseTimer(duration);
  }

  private startAutoCloseTimer(ms: number): void {
    this.clearAutoCloseTimer();
    this.autoCloseTimerId = setTimeout(() => {
      this.closeModal();
    }, ms);
  }

  private resetAutoCloseTimerForCurrentStep(): void {
    this.startAutoCloseTimerForCurrentStep();
  }

  private clearAutoCloseTimer(): void {
    if (this.autoCloseTimerId) {
      clearTimeout(this.autoCloseTimerId);
      this.autoCloseTimerId = null;
    }
  }

  private shouldIgnorePress(key: string): boolean {
    const now = Date.now();
    if (this.lastPressKey === key && now - this.lastPressTime < this.PRESS_DEBOUNCE_MS) {
      return true;
    }
    this.lastPressKey = key;
    this.lastPressTime = now;
    return false;
  }
}
